import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Modal from "./Modal"
import Icon from "../../components/Icon"
import api from "../../utils/api"
import { getApiErrorMessage } from "../../utils/errorMessage"
import {
  buildUtilityProviderRegistrationPayload,
  createUtilityProviderRegistrationForm,
} from "./utilityProviderRegistration"

const inputClass =
  "w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"

// Registration entry point for UT Bills companies, launched from the dashboard
// Register menu. Two paths: create a brand-new standalone billing company, or
// use one of the user's existing FRP management companies (which the backend
// already surfaces as a utility provider). Either way we land in the billing
// console with the chosen company selected. A screen that is already the
// console passes `onRegistered` to stay put instead of navigating.
function RegisterUtBillsCompanyModal({ registeredUserName = "", onClose, onRegistered }) {
  const navigate = useNavigate()
  const [mode, setMode] = useState("new") // 'new' | 'frp'
  const [form, setForm] = useState(() => createUtilityProviderRegistrationForm(registeredUserName))
  const [frpCompanies, setFrpCompanies] = useState([])
  const [frpLoading, setFrpLoading] = useState(false)
  const [selectedFrpId, setSelectedFrpId] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    let active = true
    setFrpLoading(true)
    api
      .get("/ut-bills/providers")
      .then((res) => {
        if (!active) return
        const providers = Array.isArray(res?.data?.providers) ? res.data.providers : []
        setFrpCompanies(providers.filter((provider) => provider.source === "management_company"))
      })
      .catch(() => {
        if (active) setFrpCompanies([])
      })
      .finally(() => {
        if (active) setFrpLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    setForm((current) => ({
      ...current,
      individualName: String(registeredUserName || "").trim(),
    }))
  }, [registeredUserName])

  const goToModule = (providerId) => {
    onClose?.()
    if (onRegistered) {
      onRegistered(providerId)
      return
    }
    navigate("/app/ut-bills", { state: { tab: "billing", providerId } })
  }

  const submitNew = async (event) => {
    event.preventDefault()
    if (submitting) return
    if (!form.entityType) {
      setError("Please select an entity type.")
      return
    }
    setSubmitting(true)
    setError("")
    try {
      const payload = buildUtilityProviderRegistrationPayload(form)
      const res = await api.post("/ut-bills/providers", payload)
      goToModule(res?.data?.provider?._id)
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to create registration."))
      setSubmitting(false)
    }
  }

  const submitFrp = () => {
    if (submitting || !selectedFrpId) return
    goToModule(selectedFrpId)
  }

  return (
    <Modal
      title="Bills &amp; Invoices Company"
      subtitle="Create a billing company or use an existing FRP company"
      icon="shop"
      onClose={onClose}
      footer={
        mode === "new" ? (
          <button
            type="submit"
            form="register-utbills-company-form"
            disabled={submitting || !form.entityType}
            className="w-full rounded-xl bg-teal-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-teal-700 disabled:opacity-60"
          >
            {submitting ? "Creating..." : "Create registration"}
          </button>
        ) : (
          <button
            type="button"
            onClick={submitFrp}
            disabled={submitting || !selectedFrpId}
            className="w-full rounded-xl bg-teal-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-teal-700 disabled:opacity-60"
          >
            Use this company
          </button>
        )
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => {
              setMode("new")
              setError("")
            }}
            className={`rounded-lg px-3 py-2 text-xs font-bold transition-colors ${
              mode === "new" ? "bg-white text-teal-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            New Registration
          </button>
          <button
            type="button"
            onClick={() => {
              setMode("frp")
              setError("")
            }}
            className={`rounded-lg px-3 py-2 text-xs font-bold transition-colors ${
              mode === "frp" ? "bg-white text-teal-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            From FRP company
          </button>
        </div>

        {error && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">{error}</p>
        )}

        {mode === "new" ? (
          <form id="register-utbills-company-form" onSubmit={submitNew} className="space-y-3">
            <div>
              <label htmlFor="ut-registration-entity-type" className="mb-1 block text-xs font-semibold text-slate-600">Entity type</label>
              <select
                id="ut-registration-entity-type"
                value={form.entityType}
                onChange={(event) => setForm({ ...form, entityType: event.target.value })}
                className={inputClass}
                required
              >
                <option value="" disabled>Select entity type</option>
                <option value="individual">Individual</option>
                <option value="business">Business (SMEs)</option>
                <option value="company">Company (LLC/LTD)</option>
              </select>
            </div>

            {form.entityType === "individual" && (
              <>
                <div>
                  <label htmlFor="ut-registration-individual-name" className="mb-1 block text-xs font-semibold text-slate-600">Name</label>
                  <input
                    id="ut-registration-individual-name"
                    value={form.individualName}
                    className={`${inputClass} bg-slate-50 text-slate-600`}
                    readOnly
                    required
                    autoFocus
                  />
                  <p className="mt-1 text-[11px] text-slate-500">This comes from your RecordLOGS profile.</p>
                </div>
                <div>
                  <label htmlFor="ut-registration-aka" className="mb-1 block text-xs font-semibold text-slate-600">A.K.A (Also Known As)</label>
                  <input
                    id="ut-registration-aka"
                    value={form.alsoKnownAs}
                    onChange={(event) => setForm({ ...form, alsoKnownAs: event.target.value })}
                    className={inputClass}
                    placeholder="Optional"
                  />
                </div>
              </>
            )}

            {form.entityType === "business" && (
              <>
                <div>
                  <label htmlFor="ut-registration-business-name" className="mb-1 block text-xs font-semibold text-slate-600">Business name</label>
                  <input
                    id="ut-registration-business-name"
                    value={form.businessName}
                    onChange={(event) => setForm({ ...form, businessName: event.target.value })}
                    className={inputClass}
                    placeholder="e.g. Riverside Water Project"
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label htmlFor="ut-registration-business-type" className="mb-1 block text-xs font-semibold text-slate-600">Type of business</label>
                  <input
                    id="ut-registration-business-type"
                    value={form.businessType}
                    onChange={(event) => setForm({ ...form, businessType: event.target.value })}
                    className={inputClass}
                    placeholder="e.g. Water supplier"
                    required
                  />
                </div>
                <div>
                  <p className="mb-1 block text-xs font-semibold text-slate-600">Location details</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div>
                      <label htmlFor="ut-registration-business-city-town" className="mb-1 block text-[11px] font-medium text-slate-500">City / Town</label>
                      <input
                        id="ut-registration-business-city-town"
                        value={form.businessCityTown}
                        onChange={(event) => setForm({ ...form, businessCityTown: event.target.value })}
                        className={inputClass}
                        placeholder="e.g. Nairobi"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="ut-registration-business-location" className="mb-1 block text-[11px] font-medium text-slate-500">Location</label>
                      <input
                        id="ut-registration-business-location"
                        value={form.businessLocation}
                        onChange={(event) => setForm({ ...form, businessLocation: event.target.value })}
                        className={inputClass}
                        placeholder="e.g. Westlands"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="ut-registration-business-area" className="mb-1 block text-[11px] font-medium text-slate-500">Area</label>
                      <input
                        id="ut-registration-business-area"
                        value={form.businessArea}
                        onChange={(event) => setForm({ ...form, businessArea: event.target.value })}
                        className={inputClass}
                        placeholder="e.g. Parklands"
                        required
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {form.entityType === "company" && (
              <>
                <div>
                  <label htmlFor="ut-registration-company-name" className="mb-1 block text-xs font-semibold text-slate-600">Company name</label>
                  <input
                    id="ut-registration-company-name"
                    value={form.companyName}
                    onChange={(event) => setForm({ ...form, companyName: event.target.value })}
                    className={inputClass}
                    placeholder="e.g. Riverside Water Project"
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label htmlFor="ut-registration-service" className="mb-1 block text-xs font-semibold text-slate-600">Service offered</label>
                  <input
                    id="ut-registration-service"
                    value={form.serviceOffered}
                    onChange={(event) => setForm({ ...form, serviceOffered: event.target.value })}
                    className={inputClass}
                    placeholder="e.g. Water supply"
                  />
                </div>
                <div>
                  <label htmlFor="ut-registration-telephone" className="mb-1 block text-xs font-semibold text-slate-600">Telephone number</label>
                  <input
                    id="ut-registration-telephone"
                    value={form.telephoneNumber}
                    onChange={(event) => setForm({ ...form, telephoneNumber: event.target.value })}
                    className={inputClass}
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label htmlFor="ut-registration-company-location" className="mb-1 block text-xs font-semibold text-slate-600">Head office location</label>
                  <input
                    id="ut-registration-company-location"
                    value={form.location}
                    onChange={(event) => setForm({ ...form, location: event.target.value })}
                    className={inputClass}
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label htmlFor="ut-registration-position" className="mb-1 block text-xs font-semibold text-slate-600">Your position in the company</label>
                  <input
                    id="ut-registration-position"
                    value={form.yourPosition}
                    onChange={(event) => setForm({ ...form, yourPosition: event.target.value })}
                    className={inputClass}
                    placeholder="e.g. Director"
                  />
                </div>
                <div>
                  <label htmlFor="ut-registration-owner" className="mb-1 block text-xs font-semibold text-slate-600">Company owner</label>
                  <input
                    id="ut-registration-owner"
                    value={form.ownerNames}
                    onChange={(event) => setForm({ ...form, ownerNames: event.target.value })}
                    className={inputClass}
                    placeholder="Owner name(s)"
                  />
                </div>
              </>
            )}
          </form>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">
              Turn one of your existing FRP management companies into a utility billing company.
            </p>
            {frpLoading ? (
              <p className="py-6 text-center text-xs text-slate-400">Loading your companies...</p>
            ) : frpCompanies.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-xs text-slate-500">
                You have no FRP management companies yet. Create one from the Register -> Property Management menu first.
              </div>
            ) : (
              <div className="space-y-1.5">
                {frpCompanies.map((company) => {
                  const active = String(company._id) === String(selectedFrpId)
                  return (
                    <button
                      key={company._id}
                      type="button"
                      onClick={() => setSelectedFrpId(company._id)}
                      className={`flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left transition-colors ${
                        active ? "border-teal-500 bg-teal-50 ring-2 ring-teal-100" : "border-slate-200 bg-white hover:bg-slate-50"
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-slate-800">{company.name}</p>
                        <p className="truncate text-xs text-slate-500">
                          {company.telephoneNumber || "No telephone"}
                          {company.location ? ` - ${company.location}` : ""}
                        </p>
                      </div>
                      {active && <Icon name="check-circle-fill" size="1rem" className="shrink-0 text-teal-600" />}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default RegisterUtBillsCompanyModal
